import React from "react";
import { useNavigate } from "react-router-dom";
import { Clock, ArrowRight, Calendar } from "lucide-react";
import { formatUpcomingClassTime } from "../utils/classScheduleUtils";

const DOT_COLORS = [
  "bg-indigo-600",
  "bg-blue-500",
  "bg-amber-500",
  "bg-emerald-500",
  "bg-purple-600",
];

const UpcomingClassesEventsSection = ({ upcomingClasses = [], onEnrolClick }) => {
  const navigate = useNavigate();

  const handleEnrol = () => {
    if (onEnrolClick) {
      onEnrolClick();
    } else {
      navigate("/dashboard/ai-lab/enrol");
    }
  };

  return (
    <div className="space-y-4 text-left">
      {/* Section Header */}
      <h3 className="text-xl sm:text-2xl font-black text-slate-900 tracking-tight">
        Upcoming Classes &amp; Events
      </h3>

      {upcomingClasses && upcomingClasses.length > 0 ? (
        <div className="space-y-3">
          {upcomingClasses.map((item, index) => {
            const dotColor = DOT_COLORS[index % DOT_COLORS.length];
            const formattedTime = formatUpcomingClassTime(item.day, item.timeSlot, item.nextDate);
            const gradeText = item.childGrade
              ? item.childGrade.toLowerCase().startsWith("class")
                ? item.childGrade
                : `Class ${item.childGrade}`
              : "Class N/A";

            return (
              <div
                key={item.childId ? `${item.childId}-${item.courseId}-${index}` : index}
                className="bg-white border border-slate-200/90 hover:border-slate-350 rounded-2xl px-5 py-3.5 sm:py-4 flex flex-col sm:flex-row sm:items-center justify-between gap-3 shadow-3xs transition-all duration-200"
              >
                {/* Left: Dot Indicator + Course Name + Subtitle (Class & Child Name) */}
                <div className="flex items-center gap-3 sm:gap-3.5 min-w-0 flex-1">
                  <span className={`w-2.5 h-2.5 rounded-full flex-shrink-0 ${dotColor}`} />
                  <div className="space-y-0.5 min-w-0 text-left">
                    <h4 className="text-sm sm:text-base font-extrabold text-slate-900 truncate leading-tight">
                      {item.courseName}
                    </h4>
                    <p className="text-xs text-slate-500 font-medium truncate">
                      {gradeText} &bull; {item.childName}
                    </p>
                  </div>
                </div>

                {/* Center: Class Time with Clock icon and (IST) */}
                <div className="flex items-center gap-2 text-xs sm:text-sm font-semibold text-slate-700 flex-shrink-0">
                  <Clock className="w-4 h-4 text-slate-400 flex-shrink-0" />
                  <span>{formattedTime}</span>
                </div>

                {/* Right: Join Class Button / Link (active 15 min prior, else deactivated) */}
                <div className="flex items-center justify-end flex-shrink-0 sm:min-w-[120px]">
                  {item.active ? (
                    <a
                      href={item.meetLink}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-1.5 text-xs sm:text-sm font-extrabold text-indigo-600 hover:text-indigo-800 transition cursor-pointer group"
                    >
                      <span>Join Class</span>
                      <ArrowRight className="w-4 h-4 group-hover:translate-x-0.5 transition-transform" />
                    </a>
                  ) : (
                    <span
                      title="Class link will become active 15 minutes before the scheduled time"
                      className="inline-flex items-center gap-1.5 text-xs sm:text-sm font-bold text-slate-350 cursor-not-allowed select-none"
                    >
                      <span>Join Class</span>
                      <ArrowRight className="w-4 h-4 opacity-50" />
                    </span>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      ) : (
        /* Empty State */
        <div className="bg-white border border-slate-200/90 rounded-2xl p-6 sm:p-7 text-center space-y-3 shadow-3xs">
          <div className="w-10 h-10 rounded-full bg-slate-100 text-slate-400 flex items-center justify-center mx-auto">
            <Calendar className="w-5 h-5" />
          </div>
          <div className="space-y-1">
            <h4 className="text-sm font-extrabold text-slate-800">
              No Scheduled Classes
            </h4>
            <p className="text-xs text-slate-400 font-medium max-w-sm mx-auto">
              Your child's upcoming live classes will appear here
            </p>
          </div>
          <div className="pt-1">
            <button
              type="button"
              onClick={handleEnrol}
              className="inline-flex items-center gap-1.5 px-4 py-2 rounded-xl bg-[#5468ff] hover:bg-[#4355f0] text-white font-bold text-xs shadow-xs transition cursor-pointer"
            >
              <span>Enrol Now</span>
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default UpcomingClassesEventsSection;
